import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from "@angular/core";
import { COMMANDE_FOURNISSEUR_LIST_DATA } from "../../models/data.model";
import { Widget, WidgetSubType } from "../../models/dashboard.model";
import { DatePipe, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { CommandeEtat } from "../../../commande/enums/etat-commande.enum";
import { Commande } from "../../../commande/models/commande.model";
import { CommandeFacadeService } from "../../../commande/services/store/commande-facade.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ConfirmationDialogComponent } from "../../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { SnackService } from "../../../../shared/services/snack-service.service";
import { CommandePilotageDialogComponent } from "../commande-pilotage-dialog/commande-pilotage-dialog.component";
import {
  CommandePilotageDialogData,
  CommandePilotageItem,
} from "../commande-pilotage-dialog/commande-pilotage.types";

const ETAT_LABEL_FOURNISSEUR: Record<string, string> = {
  [CommandeEtat.EN_ATTENTE_ENVOI]: "À envoyer",
  [CommandeEtat.EN_ATTENTE_CONFIRMATION]: "À confirmer",
  [CommandeEtat.EN_ATTENTE_LIVRAISON]: "À livrer",
  [CommandeEtat.LIVREE_PARTIELLEMENT]: "Livrée partiel.",
};

const ETAT_ORDER_FOURNISSEUR: Record<string, number> = {
  [CommandeEtat.EN_ATTENTE_ENVOI]: 1,
  [CommandeEtat.EN_ATTENTE_CONFIRMATION]: 2,
  [CommandeEtat.EN_ATTENTE_LIVRAISON]: 3,
  [CommandeEtat.LIVREE_PARTIELLEMENT]: 4,
};

interface EnrichedCommande {
  commande: Commande;
  nbLignes: number;
  isOverdue: boolean;
  daysLate: number;
}

@Component({
  selector: "vl-commande-fournisseur-list",
  standalone: true,
  imports: [
    SharedModule,
    DatePipe,
    DecimalPipe,
    ChipsComponent,
    WidgetTitleComponent,
    MatIcon,
    MatTooltip,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-commande-fournisseur-list.component.html",
  styleUrl: "./widget-commande-fournisseur-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCommandeFournisseurListComponent {
  data = input<COMMANDE_FOURNISSEUR_LIST_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  /** Onglet sélectionné (null tant qu'auto-sélection non appliquée). */
  subType = signal<WidgetSubType | null>(null);
  widgetSubType = WidgetSubType;
  router = inject(Router);
  private commandeFacade = inject(CommandeFacadeService);
  private widgetDataFacade = inject(WidgetDataFacadeService);
  private dialogs = inject(DialogsService);
  private snack = inject(SnackService);

  /** L'utilisateur a choisi un onglet manuellement → on ne re-sélectionne plus
   *  automatiquement à chaque rechargement (sinon le clic est perdu). */
  private userPickedTab = signal<boolean>(false);

  /** IDs des commandes soldées via le widget — masquées localement en attendant
   *  le prochain rechargement des données. */
  private soldedIds = signal<Set<string>>(new Set());

  /** Constante pour le template (étape "livraison partielle"). */
  readonly LIVREE_PARTIELLEMENT = CommandeEtat.LIVREE_PARTIELLEMENT;

  private countByEtat(etat: CommandeEtat): number {
    const hidden = this.soldedIds();
    return (
      this.data()?.commandesFournisseur?.filter(
        (c) => c.etat === etat && !hidden.has(c.id),
      ).length ?? 0
    );
  }

  countEnvoi = computed(() => this.countByEtat(CommandeEtat.EN_ATTENTE_ENVOI));
  countConfirmation = computed(() =>
    this.countByEtat(CommandeEtat.EN_ATTENTE_CONFIRMATION),
  );
  countLivraison = computed(() =>
    this.countByEtat(CommandeEtat.EN_ATTENTE_LIVRAISON),
  );
  countPartielle = computed(() =>
    this.countByEtat(CommandeEtat.LIVREE_PARTIELLEMENT),
  );

  /** Total brut (toutes étapes confondues) — affiché à côté du titre. */
  totalGlobal = computed(
    () =>
      this.countEnvoi() +
      this.countConfirmation() +
      this.countLivraison() +
      this.countPartielle(),
  );

  /** Liste enrichie filtrée sur l'onglet courant, triée par urgence. */
  commandesFournisseur = computed<EnrichedCommande[]>(() => {
    const sub = this.subType();
    if (!this.data() || !sub) return [];
    const etat = this.etatForSubType(sub);
    if (!etat) return [];
    const today = startOfDay(new Date());
    const hidden = this.soldedIds();
    return (this.data()?.commandesFournisseur ?? [])
      .filter((c) => c.etat === etat && !hidden.has(c.id))
      .map((c) => this.enrich(c, today))
      .sort((a, b) => {
        // Retards en premier (par jours de retard descendants),
        // puis par dateLivraisonPrevue croissante.
        if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
        if (a.isOverdue && b.isOverdue) return b.daysLate - a.daysLate;
        const da = a.commande.dateLivraisonPrevue
          ? new Date(a.commande.dateLivraisonPrevue).getTime()
          : Infinity;
        const db = b.commande.dateLivraisonPrevue
          ? new Date(b.commande.dateLivraisonPrevue).getTime()
          : Infinity;
        return da - db;
      });
  });

  /** Synthèse de l'onglet courant : nb en retard. */
  filteredStats = computed(() => {
    const list = this.commandesFournisseur();
    let overdue = 0;
    for (const e of list) {
      if (e.isOverdue) overdue++;
    }
    return { overdue, total: list.length };
  });

  constructor() {
    // Auto-sélection : à la première arrivée des données, on pointe sur le
    // premier onglet qui contient au moins un item (ordre métier).
    effect(
      () => {
        const data = this.data();
        if (!data) return;
        if (this.userPickedTab()) return;
        untracked(() => {
          const order: { sub: WidgetSubType; count: number }[] = [
            {
              sub: WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_ENVOI,
              count: this.countEnvoi(),
            },
            {
              sub: WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_CONFIRMATION,
              count: this.countConfirmation(),
            },
            {
              sub: WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_LIVRAISON,
              count: this.countLivraison(),
            },
            {
              sub: WidgetSubType.COMMANDE_FOURNISSEUR_LIVREE_PARTIELLEMENT,
              count: this.countPartielle(),
            },
          ];
          const firstWithItems = order.find((o) => o.count > 0);
          this.subType.set(
            firstWithItems
              ? firstWithItems.sub
              : WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_ENVOI,
          );
        });
      },
      { allowSignalWrites: true },
    );
  }

  selectTab(sub: WidgetSubType): void {
    this.userPickedTab.set(true);
    this.subType.set(sub);
  }

  selectCommande(elem: EnrichedCommande): void {
    this.router.navigate([`/commandes/details/id/${elem.commande.id}/commande`]);
  }

  /** Ouvre le dialog de pilotage avec KPIs, breakdown par étape/tiers et
   *  export Excel multi-feuilles. Limité aux 4 états "en cours" du widget —
   *  on n'inclut pas EN_REDACTION, ANNULEE, TERMINEE, SOLDEE. */
  goToList(): void {
    const today = startOfDay(new Date());
    const items: CommandePilotageItem[] = (
      this.data()?.commandesFournisseur ?? []
    )
      .filter((c) => ETAT_LABEL_FOURNISSEUR[c.etat] !== undefined)
      .map((c) => {
      const datePrev = c.dateLivraisonPrevue
        ? startOfDay(new Date(c.dateLivraisonPrevue))
        : null;
      const overdueRelevant =
        c.etat === CommandeEtat.EN_ATTENTE_LIVRAISON ||
        c.etat === CommandeEtat.LIVREE_PARTIELLEMENT;
      const isOverdue =
        overdueRelevant && datePrev !== null && datePrev.getTime() < today.getTime();
      const daysLate =
        isOverdue && datePrev
          ? Math.floor(
              (today.getTime() - datePrev.getTime()) / (1000 * 60 * 60 * 24),
            )
          : 0;
      return {
        id: c.id,
        numero: c.numero ?? "",
        etat: c.etat,
        etatLabel: ETAT_LABEL_FOURNISSEUR[c.etat] ?? c.etat,
        etatOrder: ETAT_ORDER_FOURNISSEUR[c.etat] ?? 99,
        tiersId: c.tiers?.id ?? null,
        tiersDenomination: c.tiers?.denomination ?? "—",
        dateCreation: (c as any).dateCreation ?? null,
        dateLivraisonPrevue: c.dateLivraisonPrevue ?? null,
        totalHT: (c as any).totalHT ?? 0,
        totalTTC: (c as any).totalTTC ?? 0,
        nbLignes: (c as any).nbLignes ?? c.lignes?.length ?? 0,
        daysLate,
      };
    });
    const dialogData: CommandePilotageDialogData = {
      mode: "fournisseur",
      items,
    };
    this.dialogs.open(
      CommandePilotageDialogComponent,
      "min(95vw, 1200px)",
      "min(90vh, 800px)",
      dialogData,
    );
  }

  /**
   * Solde une commande en livraison partielle : on demande confirmation, on
   * appelle l'API, puis on masque la carte localement et on déclenche le
   * refresh widget. Le `$event.stopPropagation()` du wrapper évite la
   * navigation vers le détail.
   */
  async onSolder(elem: EnrichedCommande): Promise<void> {
    const tiers = elem.commande.tiers?.denomination ?? "ce fournisseur";
    const dialogRef = this.dialogs.open(
      ConfirmationDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: "Solder la commande",
        message: `Solder la commande ${elem.commande.numero} (${tiers}) ? Les quantités non livrées seront définitivement abandonnées.`,
      },
    );
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) return;
    try {
      await this.commandeFacade.solder(elem.commande);
      // Optimistic hide : la carte disparaît immédiatement (sans attendre
      // l'aller-retour du refresh widget back).
      this.soldedIds.update((s) => new Set(s).add(elem.commande.id));
      this.snack.success(`Commande ${elem.commande.numero} soldée`);
      // Refresh des données widget (vide le cache back + force re-fetch). Les
      // autres widgets se re-chargent aussi — léger surcoût mais garantit la
      // cohérence (notamment le compteur global et les autres dashboards).
      void this.widgetDataFacade.refreshAll();
    } catch (e) {
      // L'intercepteur HTTP global affiche déjà l'erreur — log pour debug.
      console.error("[widget-cf] solder error", e);
    }
  }

  private etatForSubType(sub: WidgetSubType): CommandeEtat | null {
    switch (sub) {
      case WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_ENVOI:
        return CommandeEtat.EN_ATTENTE_ENVOI;
      case WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_CONFIRMATION:
        return CommandeEtat.EN_ATTENTE_CONFIRMATION;
      case WidgetSubType.COMMANDE_FOURNISSEUR_EN_ATTENTE_LIVRAISON:
        return CommandeEtat.EN_ATTENTE_LIVRAISON;
      case WidgetSubType.COMMANDE_FOURNISSEUR_LIVREE_PARTIELLEMENT:
        return CommandeEtat.LIVREE_PARTIELLEMENT;
      default:
        return null;
    }
  }

  private enrich(c: Commande, today: Date): EnrichedCommande {
    const datePrev = c.dateLivraisonPrevue
      ? startOfDay(new Date(c.dateLivraisonPrevue))
      : null;
    // Le retard ne s'applique qu'aux états où une livraison est attendue.
    const overdueRelevant =
      c.etat === CommandeEtat.EN_ATTENTE_LIVRAISON ||
      c.etat === CommandeEtat.LIVREE_PARTIELLEMENT;
    const isOverdue =
      overdueRelevant && datePrev !== null && datePrev.getTime() < today.getTime();
    const daysLate =
      isOverdue && datePrev
        ? Math.floor((today.getTime() - datePrev.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
    return {
      commande: c,
      nbLignes: c.lignes?.length ?? 0,
      isOverdue,
      daysLate,
    };
  }
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
